import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Share2, Users, ArrowUpRight, ListPlus, Inbox } from 'lucide-react'
import { getSharedWithMe } from '../../services/api'
import type { SearchResult } from '../../types'
import AddToListModal from '../../components/lists/AddToListModal'

type TabValue = 'received' | 'sent'

function isJournalArticle(r: SearchResult): boolean {
  return 'record_id' in r
}

function getResourceId(r: SearchResult): string {
  return 'record_id' in r ? (r as any).record_id : (r as any).resource_id
}

function getResourceType(r: SearchResult): string {
  return 'resource_type' in r ? (r as any).resource_type : 'journal'
}

export default function SharedWithMe() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabValue>('received')
  const [addToListResource, setAddToListResource] = useState<{
    id: string
    type: string
    title: string
  } | null>(null)

  const { data: sharedResources = [], isLoading } = useQuery({
    queryKey: ['sharedWithMe'],
    queryFn: getSharedWithMe,
  })

  const tabs: { value: TabValue; label: string; icon: typeof Inbox }[] = [
    { value: 'received', label: 'Shared With Me', icon: Inbox },
    { value: 'sent', label: 'Shared By Me', icon: Share2 },
  ]

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Shared Resources</h1>
        <p className="text-gray-500 mt-1">
          Resources shared between you and other researchers
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.value
                ? 'border-acad-500 text-acad-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 p-5 animate-pulse">
              <div className="h-5 bg-gray-200 rounded w-3/4 mb-3" />
              <div className="flex gap-4">
                <div className="h-3 bg-gray-200 rounded w-20" />
                <div className="h-3 bg-gray-200 rounded w-32" />
                <div className="h-3 bg-gray-200 rounded w-24" />
              </div>
            </div>
          ))}
        </div>
      ) : sharedResources.length === 0 ? (
        <div className="text-center py-20">
          <div className="mx-auto w-20 h-20 rounded-full bg-acad-50 flex items-center justify-center mb-4">
            <Users className="h-10 w-10 text-acad-300" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            {activeTab === 'received'
              ? 'No resources have been shared with you yet'
              : 'You have not shared any resources yet'}
          </h3>
          <p className="text-gray-500 max-w-sm mx-auto">
            {activeTab === 'received'
              ? 'When someone shares a resource with you, it will appear here.'
              : 'Use the Share button on any resource to share it with colleagues.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {sharedResources.map((resource: SearchResult) => {
            const resourceId = getResourceId(resource)
            const resourceType = getResourceType(resource)
            const isJournal = isJournalArticle(resource)

            return (
              <div
                key={resourceId}
                className="bg-white rounded-lg border border-gray-200 p-5 hover:border-acad-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="inline-flex items-center rounded-full bg-acad-50 px-2 py-0.5 text-xs font-medium text-acad-600 capitalize">
                        {isJournal ? 'journal' : resourceType}
                      </span>
                    </div>
                    <h3
                      className="text-base font-medium text-gray-900 hover:text-acad-600 cursor-pointer"
                      onClick={() => navigate(`/resource/${resourceId}`)}
                    >
                      {resource.title}
                    </h3>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                      {isJournal && (resource as any).authors?.length > 0 && (
                        <span>{(resource as any).authors.slice(0, 2).join(', ')}</span>
                      )}
                      {!isJournal && (resource as any).creator && (
                        <span>{(resource as any).creator}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() =>
                        setAddToListResource({
                          id: resourceId,
                          type: resourceType,
                          title: resource.title,
                        })
                      }
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      title="Add to My List"
                    >
                      <ListPlus className="h-3.5 w-3.5" />
                      Add to List
                    </button>
                    <button
                      onClick={() => navigate(`/resource/${resourceId}`)}
                      className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      title="View resource"
                    >
                      <ArrowUpRight className="h-3.5 w-3.5" />
                      View
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Add to List Modal */}
      {addToListResource && (
        <AddToListModal
          isOpen={!!addToListResource}
          onClose={() => setAddToListResource(null)}
          resourceId={addToListResource.id}
          resourceType={addToListResource.type}
          title={addToListResource.title}
        />
      )}
    </div>
  )
}
